import React, { useState } from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import { Add as AddIcon } from '@material-ui/icons'

import { useQueryPaginator } from 'src/components/Table'
import { CarrierDetailTable } from 'src/components/Table/detail/Carrier'
import { CarrierDialog } from 'src/modules/supply_chain/components/CarrierDialog'
import { AppointmentListTable } from 'src/components/Table/list/Appointment'
import { ASNListTable } from 'src/components/Table/list/ASN'
import { EventListTable } from 'src/components/Table/list/Event'
import { FreightShipmentListTable } from 'src/components/Table/list/FreightShipment'
import { ParcelShipmentListTable } from 'src/components/Table/list/ParcelShipment'
import { TruckloadListTable } from 'src/components/Table/list/Truckload'

import { skuit } from 'src/services/skuit'
import { useQuery } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'
import { DetailTab } from 'src/components/Pages/Tab'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathProps {
  guid: string
}

const InboundTab = () => {
  const { guid } = useParams<PathProps>()

  const asnPaginator = useQueryPaginator('appointment-list', skuit.sc.asn.list, {
    params: { page_size: 5, carriers: guid },
  })

  return (
    <DetailTab>
      <ASNListTable
        paginator={asnPaginator}
        fields={[
          'trackingNumber',
          'client',
          'consignee',
          'facility',
          'status',
          'transloadNumber',
          'appointment',
          'dates',
        ]}
      />
    </DetailTab>
  )
}

const FreightTab = () => {
  const { guid } = useParams<PathProps>()

  const shipmentsPaginator = useQueryPaginator(
    'freight-shipment-list',
    skuit.sc.freight.shipments.list,
    {
      params: { page_size: 5, carriers: guid },
    }
  )
  const truckloadsPaginator = useQueryPaginator(
    'truckload-list',
    skuit.sc.freight.truckloads.list,
    {
      params: { page_size: 5, carriers: guid },
    }
  )

  return (
    <DetailTab>
      <FreightShipmentListTable
        paginator={shipmentsPaginator}
        fields={['trackingNumber', 'client', 'order', 'consignee', 'facility', 'status']}
      />
      <TruckloadListTable
        paginator={truckloadsPaginator}
        fields={[
          'trackingNumber',
          'billOfLadingNumber',
          'trailerNumber',
          'client',
          'facility',
          'status',
          'appointment',
          'numOrders',
        ]}
      />
    </DetailTab>
  )
}

const ParcelTab = () => {
  const { guid } = useParams<PathProps>()

  const shipmentsPaginator = useQueryPaginator(
    'parcel-shipment-list',
    skuit.sc.parcel.shipments.list,
    {
      params: { page_size: 5, carriers: guid },
    }
  )

  return (
    <DetailTab>
      <ParcelShipmentListTable
        paginator={shipmentsPaginator}
        fields={['trackingNumber', 'client', 'consignee', 'facility', 'status']}
      />
    </DetailTab>
  )
}

const AppointmentsTab = () => {
  const { guid } = useParams<PathProps>()

  const appointmentsPaginator = useQueryPaginator('appointment-list', skuit.sc.appointments.list, {
    params: { page_size: 5, brokers: guid },
  })

  return (
    <DetailTab>
      <AppointmentListTable
        paginator={appointmentsPaginator}
        fields={['name', 'start', 'end', 'facility', 'status']}
      />
    </DetailTab>
  )
}

const EventsTab = () => {
  const { guid } = useParams<PathProps>()

  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  return (
    <DetailTab>
      <EventListTable
        paginator={eventsPaginator}
        title="Carrier Events"
        fields={['type', 'data', 'by', 'on']}
      />
    </DetailTab>
  )
}

export const CarrierPage = () => {
  const { guid } = useParams<PathProps>()
  const [createOpen, setCreateOpen] = useState(false)

  const carrier = useQuery(['carrier-details', guid], () => skuit.sc.carriers.retrieve(guid))
  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  const refresh = () => {
    carrier.refetch()
    eventsPaginator.refresh()
  }

  return (
    <>
      {createOpen && (
        <CarrierDialog
          open={createOpen}
          handleClose={() => {
            setCreateOpen(false)
          }}
        />
      )}
      <DetailPage
        obj={carrier}
        title="Carriers"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'Carriers', url: '/shipping/carriers' },
        ]}
        detailComponent={<CarrierDetailTable obj={carrier} refresh={refresh} />}
        menuItems={[
          {
            Icon: AddIcon,
            actions: [{ title: 'New Carrier', onClick: () => setCreateOpen(true) }],
          },
        ]}
        tabs={['Inbound', 'Freight', 'Parcel', 'Brokered Appointments', 'Events']}
      >
        <InboundTab />
        <FreightTab />
        <ParcelTab />
        <AppointmentsTab />
        <EventsTab />
      </DetailPage>
    </>
  )
}
